import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
    AdjacentNewsArticlesResponse,
    AuctionCatalogsService,
    NewsArticlePublicResponse,
} from '@viewbid/ts-angular-client';
import { VbNewsArticle } from '@viewbid/ui';
import { VbSnackbarService } from '@viewbid/ui';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { mapNewsArticleDetail } from '../../utils/news-ui-mapper.util';

@Component({
    selector: 'vb-news-article-page',
    templateUrl: './news-article-page.component.html',
    styleUrls: ['./news-article-page.component.scss'],
})
export class NewsArticlePageComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    public article: VbNewsArticle | null = null;
    public neighbours: AdjacentNewsArticlesResponse | null = null;
    public isLoading: boolean = true;
    public isFailed: boolean = false;

    /** Parent crumb for the breadcrumb bar: Home › News › {article title}. */
    public readonly newsParent: { label: string; commands: string[] }[] = [
        { label: 'News', commands: ['/news'] },
    ];

    constructor(
        private auctionCatalogService: AuctionCatalogsService,
        private snackbarService: VbSnackbarService,
        private translateService: TranslateService,
        private route: ActivatedRoute,
    ) {}

    ngOnInit(): void {
        const articleIds: Observable<string | null> = this.route.paramMap.pipe(
            map((params: ParamMap): string | null => params.get('articleId')),
        );
        articleIds
            .pipe(
                switchMap((articleId: string | null) =>
                    this.fetchArticle(articleId),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((response: NewsArticlePublicResponse) => {
                this.article = mapNewsArticleDetail(response);
                this.isLoading = false;
            });
        articleIds
            .pipe(
                switchMap((articleId: string | null) =>
                    this.fetchNeighbours(articleId),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((neighbours: AdjacentNewsArticlesResponse) => {
                this.neighbours = neighbours;
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private fetchArticle(
        articleId: string | null,
    ): Observable<NewsArticlePublicResponse> {
        this.article = null;
        this.isLoading = true;
        this.isFailed = false;
        if (!articleId) {
            this.isLoading = false;
            this.isFailed = true;
            return EMPTY;
        }
        return this.auctionCatalogService.getPublishedNewsById(articleId).pipe(
            catchError(() => {
                this.snackbarService.error(
                    this.translateService.instant(
                        'newsArticle.errorFetchingArticles',
                    ),
                );
                this.isFailed = true;
                this.isLoading = false;
                return EMPTY;
            }),
        );
    }

    private fetchNeighbours(
        articleId: string | null,
    ): Observable<AdjacentNewsArticlesResponse> {
        this.neighbours = null;
        if (!articleId) return EMPTY;
        return this.auctionCatalogService
            .getAdjacentPublishedNews(articleId)
            .pipe(catchError(() => EMPTY));
    }
}
